import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { VolSurfaceToken } from '../../../model/currency_type.enum';
import { RangeEnum } from '../../../model/range.enum';
import Utils from '../../../utils/immersive_utils';

import { CandleRequest } from '../../model/candleData';
import { QueryData } from '../../model/interfaces/volume_data';
import { VolumeMultipleRequest } from '../../model/volumeData';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { WidgetContext } from '../../../components/v2/widget-renderer/widget-provider';
import usePersistentState from '../../use-persistent-state';

interface ScatterPlotProviderProps {
  children: ReactNode;
  token?: VolSurfaceToken;
  range?: RangeEnum;
  providerKey?: string;
}

interface ScatterPlotContextType {
  data: any;
  xAxis: string;
  setXAxis: (value: string) => void;
  yAxis: string;
  setYAxis: (value: string) => void;
  loading: boolean;
  range: RangeEnum;
  setRange: (range: RangeEnum) => void;
  token: VolSurfaceToken;
  setToken: React.Dispatch<React.SetStateAction<VolSurfaceToken>>;
  hasDefaultToken: boolean;
}

const KEY_X_BASE = 'SCATTER_PLOT_X';
const KEY_Y_BASE = 'SCATTER_PLOT_Y';

const CANDLE_SUBSCRIPTION = 'CANDLE_SUBSCRIPTION';
export const CHART_EVENTS = [CANDLE_SUBSCRIPTION];

export const ScatterPlotContext = createContext({} as ScatterPlotContextType);

export const ScatterPlotProvider = ({ children, ...props }: ScatterPlotProviderProps) => {
  const STATE_PATH = ['SCATTER_PLOT_PROVIDER', props.providerKey || 'DEFAULT'];

  const { subscribeToChannel, sendMessage, isConnected } = useContext(WebSocketContext);
  const [data, setData] = useState({
    x: null,
    y: null,
  });

  const [xAxis, setXAxis] = usePersistentState(STATE_PATH, 'xAxis', 'spot');
  const [yAxis, setYAxis] = usePersistentState(STATE_PATH, 'yAxis', 'ATMF_1W');
  const [range, setRange] = usePersistentState(STATE_PATH, 'range', RangeEnum.D1);

  const [requestsResponse, setRequestsResponse] = useState(0);

  const { token, setToken, hasDefaultToken } = useContext(WidgetContext);

  const KEY_X = `${props.providerKey || ''}_${KEY_X_BASE}`;
  const KEY_Y = `${props.providerKey || ''}_${KEY_Y_BASE}`;

  useEffect(() => {
    if (requestsResponse === 2) {
      setLoading(false);
      setRequestsResponse(0);
      return;
    }
  }, [requestsResponse]);

  const handleResponseX = useCallback(
    (response: any) => {
      if (!response) return;

      const values = response.result.map((item: any) => item[1]);

      setData((prev) => ({
        ...prev,
        x: values,
      }));
      setRequestsResponse((prev) => prev + 1);
    },
    [requestsResponse],
  );

  const handleResponseY = useCallback(
    (response: any) => {
      if (!response) return;

      const values = response.result.map((item: any) => item[1]);

      setData((prev) => ({
        ...prev,
        y: values,
      }));
      setRequestsResponse((prev) => prev + 1);
    },
    [requestsResponse],
  );

  const initTimeStamp = Utils.getInitDateByRange(new Date(), range).toISOString();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    subscribeToChannel(KEY_X, handleResponseX);
    subscribeToChannel(KEY_Y, handleResponseY);
  }, []);

  const getVolData = useCallback(
    (endDate: Date, id: string, d: QueryData[], token: string) => {
      const interval = Utils.getIntervalByRange(range);

      const date = Utils.updateDateToInterval(endDate, interval);

      const initDate =
        initTimeStamp < endDate.toISOString()
          ? initTimeStamp
          : Utils.getInitDateByRange(endDate, range).toISOString();

      const volRequest = new VolumeMultipleRequest(
        id,
        initDate,
        date.toISOString(),
        Utils.getIntervalByRange(range),
        'DERIBIT',
        token,
        d,
        'SVI',
      );

      sendMessage(JSON.stringify(volRequest));
    },
    [initTimeStamp, range],
  );

  const getSpotData = useCallback(
    (endDate: Date, id: string, token: string) => {
      const interval = Utils.getIntervalByRange(range);

      const date = Utils.updateDateToInterval(endDate, interval);
      const initDate =
        initTimeStamp < endDate.toISOString()
          ? initTimeStamp
          : Utils.getInitDateByRange(endDate, range).toISOString();

      const candleRequest = new CandleRequest(
        id,
        initDate,
        date.toISOString(),
        Utils.getIntervalByRange(range),
        'DERIBIT',
        token,
        'SPOT',
      );

      sendMessage(JSON.stringify(candleRequest));
    },
    [initTimeStamp, range],
  );

  const handleChangeVolData = useCallback(() => {
    if (isConnected) {
      const getLabelTenor = (axis: string) => {
        const spplited = axis.split('_');

        if (spplited.length === 3) return { label: spplited[0], tenor: spplited[2] };

        return { label: spplited[0], tenor: spplited[1] };
      };
      setLoading(true);

      const endDate = new Date(new Date());
      endDate.setSeconds(0);
      endDate.setMilliseconds(0);

      if (xAxis !== 'spot') {
        getVolData(endDate, KEY_X, [getLabelTenor(xAxis)], token);
      } else {
        getSpotData(endDate, KEY_X, token);
      }

      if (yAxis !== 'spot') {
        getVolData(endDate, KEY_Y, [getLabelTenor(yAxis)], token);
      } else {
        getSpotData(endDate, KEY_Y, token);
      }
    }
  }, [xAxis, yAxis, token, range, isConnected]);

  useEffect(() => {
    if (isConnected) {
      handleChangeVolData();
    }
  }, [xAxis, yAxis, token, range, isConnected]);

  return (
    <ScatterPlotContext.Provider
      value={{
        data,
        xAxis,
        setXAxis,
        yAxis,
        setYAxis,
        loading,
        range,
        setRange,
        token,
        setToken,
        hasDefaultToken,
      }}
    >
      {children}
    </ScatterPlotContext.Provider>
  );
};
